import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { THEME_PRESETS } from "../config/themePresets";
import { Lock, Sparkles, ArrowLeft } from "lucide-react";

const FONTS = ["Outfit", "Manrope", "Inter", "Playfair Display", "DM Sans", "Space Grotesk"];
const LAYOUTS = ["grid", "list", "cards"];
const BUTTON_STYLES = ["rounded", "sharp", "pill"];

export default function ThemeCustomizer() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [theme, setTheme] = useState(null);
  const [items, setItems] = useState([]);

  const isPremium = user?.role === "admin" || ["pro", "premium", "agency"].includes(user?.plan);

  const load = async () => {
    const [r, i] = await Promise.all([api.get(`/restaurants/${id}`), api.get(`/restaurants/${id}/items`)]);
    setRestaurant(r.data);
    setTheme(r.data.theme);
    setItems(i.data.slice(0, 4));
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    await api.put(`/restaurants/${id}/theme`, theme);
    toast.success(t("common.success"));
  };

  const applyPreset = (preset) => {
    if (preset.premium && !isPremium) {
      toast.error(lang === "de" ? "Dieses Theme ist Pro-exklusiv" : lang === "tr" ? "Bu tema Pro'ya özeldir" : "This theme is Pro-only");
      return;
    }
    setTheme({ ...theme, ...preset.theme });
    toast.success(`✨ ${preset.name[lang] || preset.name.de}`);
  };

  if (!theme) return <DashboardLayout restaurantId={id}><div>{t("common.loading")}</div></DashboardLayout>;

  const radius = theme.button_style === "pill" ? "9999px" : theme.button_style === "sharp" ? "0" : "12px";
  const symbol = { EUR: "€", USD: "$", TRY: "₺", AED: "AED" }[restaurant?.currency || "EUR"];

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("theme.title")}</div>
            <h1 className="text-3xl font-bold tracking-tight">{restaurant?.name}</h1>
          </div>
          <Link
            to={`/dashboard/restaurants/${id}/themes`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-semibold transition-colors"
            data-testid="link-back-to-themes"
          >
            <ArrowLeft className="h-4 w-4" />
            {lang === "de" ? "Zurück zu Themes" : lang === "tr" ? "Temalara dön" : "Back to Themes"}
          </Link>
        </div>

        {/* Theme presets */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                {lang === "de" ? "Vorgefertigte Themes" : lang === "tr" ? "Hazır Temalar" : "Theme presets"}
              </div>
              <div className="text-xs text-muted-foreground">
                {lang === "de" ? "Wähle einen Stil oder passe alles unten an" : lang === "tr" ? "Bir stil seç veya aşağıdan özelleştir" : "Pick a style or customize below"}
              </div>
            </div>
            {!isPremium && (
              <Link to="/pricing" className="text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline">
                {lang === "de" ? "Pro freischalten →" : lang === "tr" ? "Pro'ya geç →" : "Unlock with Pro →"}
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {THEME_PRESETS.map((p) => {
              const locked = p.premium && !isPremium;
              return (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  className={`group relative text-left rounded-xl border p-2.5 transition-all hover:-translate-y-0.5 hover:shadow-md ${locked ? "opacity-60 hover:opacity-90" : "hover:border-purple-500/40"}`}
                  data-testid={`theme-preset-${p.id}`}
                  type="button"
                >
                  <div
                    className="h-14 w-full rounded-lg mb-2"
                    style={{ background: `linear-gradient(135deg, ${p.theme.primary_color}, ${p.theme.accent_color})` }}
                  />
                  <div className="text-xs font-bold leading-tight">{p.name[lang] || p.name.de}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{p.description[lang] || p.description.de}</div>
                  {p.premium && locked && (
                    <span className="absolute top-1.5 right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                      <Lock className="h-2.5 w-2.5 inline" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border bg-card p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                ["primary_color", "primaryColor"],
                ["accent_color", "accentColor"],
                ["background_color", "backgroundColor"],
                ["text_color", "textColor"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{t(`theme.${label}`)}</Label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={theme[key]} onChange={(e) => setTheme({ ...theme, [key]: e.target.value })} className="w-12 h-10 rounded-lg border cursor-pointer" data-testid={`theme-${key}`} />
                    <input value={theme[key]} onChange={(e) => setTheme({ ...theme, [key]: e.target.value })} className="flex-1 h-10 rounded-xl border bg-background px-3 text-sm font-mono" />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>{t("theme.fontFamily")}</Label>
              <select className="w-full h-10 rounded-xl border bg-background px-3" value={theme.font_family} onChange={(e) => setTheme({ ...theme, font_family: e.target.value })} data-testid="theme-font">
                {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("theme.layout")}</Label>
                <div className="flex gap-2">
                  {LAYOUTS.map((l) => (
                    <button key={l} onClick={() => setTheme({ ...theme, layout: l })} className={`flex-1 px-3 py-2 rounded-xl border text-sm capitalize ${theme.layout === l ? "bg-purple-600 text-white border-purple-600" : "bg-background"}`} data-testid={`theme-layout-${l}`}>{t(`theme.${l}`)}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("theme.buttonStyle")}</Label>
                <div className="flex gap-2">
                  {BUTTON_STYLES.map((b) => (
                    <button key={b} onClick={() => setTheme({ ...theme, button_style: b })} className={`flex-1 px-3 py-2 rounded-xl border text-sm capitalize ${theme.button_style === b ? "bg-purple-600 text-white border-purple-600" : "bg-background"}`} data-testid={`theme-btn-${b}`}>{t(`theme.${b}`)}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <Label>{t("common.darkMode")}</Label>
              <Switch checked={theme.dark_mode} onCheckedChange={(v) => setTheme({ ...theme, dark_mode: v })} />
            </div>

            {/* White-label toggle (Pro+) */}
            <div className={`flex items-center justify-between rounded-xl border p-3 ${!isPremium ? "opacity-70" : ""}`}>
              <div className="flex-1">
                <Label className="flex items-center gap-2">
                  {lang === "de" ? "qrmenu-pro.com Branding ausblenden" : lang === "tr" ? "qrmenu-pro.com markasını gizle" : "Hide qrmenu-pro.com branding"}
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-400">PRO</span>
                </Label>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {lang === "de" ? "Entfernt „Powered by\" aus dem öffentlichen Menü" : lang === "tr" ? "Genel menüden „Powered by\" yazısını kaldırır" : "Removes the \"Powered by\" footer from the public menu"}
                </div>
              </div>
              <Switch
                checked={!!theme.hide_branding}
                onCheckedChange={(v) => {
                  if (!isPremium) {
                    toast.error(lang === "de" ? "Diese Funktion ist Pro-exklusiv" : lang === "tr" ? "Bu özellik Pro'ya özeldir" : "This feature is Pro-only");
                    return;
                  }
                  setTheme({ ...theme, hide_branding: v });
                }}
                disabled={!isPremium}
                data-testid="theme-hide-branding"
              />
            </div>

            <Button onClick={save} className="w-full rounded-full bg-purple-600 text-white" data-testid="theme-save">{t("common.save")}</Button>
          </div>

          {/* Mobile preview */}
          <div className="grid place-items-center">
            <div className="w-[300px] h-[600px] rounded-[40px] bg-zinc-900 p-3 shadow-2xl shadow-purple-500/20 border border-zinc-800" data-testid="theme-preview">
              <div className="absolute" />
              <div className="w-full h-full rounded-[28px] overflow-hidden relative" style={{ backgroundColor: theme.background_color, color: theme.text_color, fontFamily: `'${theme.font_family}', sans-serif` }}>
                <div className="h-32 relative" style={{ background: `linear-gradient(135deg, ${theme.primary_color}, ${theme.accent_color})` }}>
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">Preview</div>
                    <div className="text-lg font-bold">{restaurant?.name}</div>
                  </div>
                </div>
                <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: "calc(100% - 8rem)" }}>
                  {items.map((it) => (
                    <div key={it.id} className="p-2.5 border" style={{ borderColor: "rgba(0,0,0,0.1)", borderRadius: radius }}>
                      <div className="flex justify-between gap-2">
                        <div className="text-xs font-bold">{it.name}</div>
                        <div className="text-xs font-bold" style={{ color: theme.primary_color }}>{it.price.toFixed(2)} {symbol}</div>
                      </div>
                      <div className="text-[10px] opacity-60 mt-0.5">{it.description}</div>
                    </div>
                  ))}
                  <button className="w-full py-2 text-xs font-bold text-white" style={{ background: theme.primary_color, borderRadius: radius }}>
                    Order Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
